import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

export interface User {
  id: number
  username: string
  email: string
  firstname?: string
  lastname?: string
  role: number
  status: number
  selected_account_id?: number
  advertiser_id?: number
  default_language: string
  position?: string
  picture_file: string
  backend_access: boolean
  landing_page?: string
  privileges?: string[]
  is_superadmin?: boolean
  two_factor_enabled?: number
  two_factor_method?: 'email' | 'totp' | null
  // DD-1771 — 2FA obligatorio por alguna de sus cuentas (lo deriva /auth/profile): el propio
  // usuario no lo puede desactivar.
  two_factor_required?: number
}

export const useUserStore = defineStore('user', () => {
  const user = ref<User | null>(null)
  const isAuthenticated = ref(false)
  const loading = ref(false)

  const hasPrivilege = computed(() => (name: string) => {
    if (!user.value) return false
    if (user.value.is_superadmin) return true
    return user.value.privileges?.includes(name) ?? false
  })

  const canViewAudit = computed(() =>
    user.value?.is_superadmin === true ||
    (user.value?.privileges?.includes('audit/index') ?? false)
  )

  // DD-1470 — gates the UI for the two actions the server now restricts to superadmins
  // (disable-2fa and change-password for another user): hide, don't let the user click through
  // to a 403.
  const isSuperAdmin = computed(() => user.value?.is_superadmin === true)

  const setUser = (userData: User | null) => {
    user.value = userData
    isAuthenticated.value = !!userData
  }

  const logout = () => {
    user.value = null
    isAuthenticated.value = false
  }

  const setLoading = (loadingState: boolean) => {
    loading.value = loadingState
  }

  return {
    user,
    isAuthenticated,
    loading,
    hasPrivilege,
    canViewAudit,
    isSuperAdmin,
    setUser,
    logout,
    setLoading
  }
})
